import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { SearchInput } from '~/components/common/search-input';
import { Button } from '~/components/ui/button';
import { MultiSelect } from '~/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';

import type {
  AspectRollup,
  GapFilters,
  GapSort,
  GapStatus,
  VolumeBand,
} from './gap-analysis.utils';
import { EMPTY_GAP_FILTERS, countByStatus, hasActiveFilters } from './gap-analysis.utils';

/** Project convention — search inputs settle before they drive anything. */
const SEARCH_DEBOUNCE_MS = 500;

const STATUS_CHIPS: { status: GapStatus; label: string }[] = [
  { status: 'added', label: 'New' },
  { status: 'restated', label: 'Restated' },
  { status: 'had', label: 'Already there' },
];

const VOLUME_BANDS: { value: VolumeBand; label: string }[] = [
  { value: 'all', label: 'Any search volume' },
  { value: 'high', label: '10k+ per month' },
  { value: 'mid', label: '1k–10k per month' },
  { value: 'low', label: 'Under 1k per month' },
];

const COMPETITION_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

interface Props {
  /** Unfiltered rollups — the chip counts describe the whole product, so a chip
   *  never reads zero merely because another filter is active. */
  rollups: AspectRollup[];
  filters: GapFilters;
  onChange: (filters: GapFilters) => void;
  /** Gates the three keyword controls. With no keyword index they would filter
   *  on a column that is empty for every row. */
  showKeywordFilters: boolean;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * Filter row above the attribute comparison. Drives both the comparison and the
 * full table; the stat tiles and charts deliberately stay unfiltered, since they
 * are the figures quoted for the product as a whole.
 */
export function GapFilterBar({
  rollups,
  filters,
  onChange,
  showKeywordFilters,
  testId = 'gap-analysis-filter',
}: Props) {
  const counts = countByStatus(rollups);
  const isFiltered = hasActiveFilters(filters);

  // The committed query lives in the parent; this mirrors keystrokes locally so
  // the field stays responsive while the filter itself settles.
  const [draftQuery, setDraftQuery] = useState(filters.query);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Keeps the box in step when the query is cleared from outside ("Clear all",
  // or the empty-state's "Clear filters"). Adjusting state during render is the
  // documented pattern for deriving from props — an effect here would cascade.
  const [lastQuery, setLastQuery] = useState(filters.query);
  if (filters.query !== lastQuery) {
    setLastQuery(filters.query);
    if (filters.query === '') setDraftQuery('');
  }

  function handleQueryChange(value: string) {
    setDraftQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...filters, query: value });
    }, SEARCH_DEBOUNCE_MS);
  }

  /** Chips are additive — selecting none means "no status filter". */
  function toggleStatus(status: GapStatus) {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((entry) => entry !== status)
      : [...filters.statuses, status];
    onChange({ ...filters, statuses: next });
  }

  const categoryOptions = rollups.map((rollup) => ({
    value: rollup.aspect,
    label: rollup.aspect.charAt(0).toUpperCase() + rollup.aspect.slice(1),
  }));

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-bar`}>
      <SearchInput
        value={draftQuery}
        onChange={handleQueryChange}
        placeholder="Search attributes…"
        className="w-full sm:w-56"
        testId={`${testId}-search`}
        inputClassName="h-8 text-sm"
      />

      {STATUS_CHIPS.map(({ status, label }) => {
        const count = counts[status];
        const isActive = filters.statuses.includes(status);
        return (
          <Button
            key={status}
            data-testid={`${testId}-status-toggle-${status}`}
            variant="outline"
            size="sm"
            disabled={count === 0}
            aria-pressed={isActive}
            onClick={() => {
              toggleStatus(status);
            }}
            className={cn(
              'h-8 rounded-full',
              count === 0 ? 'cursor-not-allowed' : 'cursor-pointer',
              // The `outline` variant ships `hover:text-accent-foreground`
              // (near-black). Without an explicit hover text colour here, an
              // active chip's label goes black-on-blue the moment it's hovered.
              isActive &&
                'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
            )}
          >
            {label}
            <span
              className="ml-1 tabular-nums opacity-70"
              data-testid={`${testId}-status-toggle-${status}-value`}
            >
              {count}
            </span>
          </Button>
        );
      })}

      <MultiSelect
        options={categoryOptions}
        value={filters.categories}
        onChange={(categories) => {
          onChange({ ...filters, categories });
        }}
        placeholder="All categories"
        searchPlaceholder="Find a category…"
        className="h-8 w-full sm:w-48"
        testId={`${testId}-category-select`}
      />

      {showKeywordFilters ? (
        <>
          <MultiSelect
            options={COMPETITION_OPTIONS}
            value={filters.competition}
            onChange={(competition) => {
              onChange({ ...filters, competition });
            }}
            placeholder="Any competition"
            searchPlaceholder="Find a level…"
            className="h-8 w-full sm:w-44"
            testId={`${testId}-competition-select`}
          />

          <Select
            value={filters.volumeBand}
            onValueChange={(volumeBand) => {
              onChange({ ...filters, volumeBand: volumeBand as VolumeBand });
            }}
          >
            <SelectTrigger
              className="h-8 w-full cursor-pointer sm:w-44"
              data-testid={`${testId}-volume-select`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid={`${testId}-volume-select-content`}>
              {VOLUME_BANDS.map((band) => (
                <SelectItem
                  key={band.value}
                  value={band.value}
                  className="cursor-pointer"
                  data-testid={`${testId}-volume-option-${band.value}`}
                >
                  {band.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : null}

      <Select
        value={filters.sort}
        onValueChange={(sort) => {
          onChange({ ...filters, sort: sort as GapSort });
        }}
      >
        <SelectTrigger
          className="h-8 w-full cursor-pointer sm:w-40"
          data-testid={`${testId}-sort-select`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-sort-select-content`}>
          <SelectItem
            value="default"
            className="cursor-pointer"
            data-testid={`${testId}-sort-option-default`}
          >
            Default order
          </SelectItem>
          <SelectItem
            value="new"
            className="cursor-pointer"
            data-testid={`${testId}-sort-option-new`}
          >
            Most new first
          </SelectItem>
          {showKeywordFilters ? (
            <SelectItem
              value="volume"
              className="cursor-pointer"
              data-testid={`${testId}-sort-option-volume`}
            >
              Search volume
            </SelectItem>
          ) : null}
          <SelectItem
            value="alpha"
            className="cursor-pointer"
            data-testid={`${testId}-sort-option-alpha`}
          >
            A–Z
          </SelectItem>
        </SelectContent>
      </Select>

      {isFiltered ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onChange({ ...EMPTY_GAP_FILTERS, sort: filters.sort });
          }}
          className="h-8 cursor-pointer gap-1.5"
          data-testid={`${testId}-clear-all-button`}
        >
          <X className="size-3.5" aria-hidden="true" />
          Clear all
        </Button>
      ) : null}
    </div>
  );
}
